/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : TicketsTable.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { TICKETS_TYPES, PROJECT_STATUS } from "../../../config/appConstants";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  Select,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import softwareIntersectionAPI from "../../../redux/actions/softwareIntersectionActions";
import {
  HandleApiActions,
  labCar,
  profileActions,
  userManagementApis,
} from "../../../redux/actions";
import { componentStyle } from "../../../config/appTheme";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import { ConvertUTCToDate } from "../../../utils/convertUtcToTimeZone";
import { useNavigate } from "react-router-dom";
import ModuleViewMoreDialog from "../../../components/customComponentsV2/dashboard/pages/tickets/ModuleViewMoreDialog";
import CustomDataGridV2 from "../../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomCheckBoxV2 from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "react-toastify";
import TicketAttachment from "./TicketAttachment";
import typeApprovalAPI from "../../../redux/actions/typeApprovalAPI";
import CustomToolTip from "../../../components/infoButton/CustomToolTip";

function VehicleSystemCell({ row, supplier, onViewMore, formatSupplierName }) {
  const vsList = React.useMemo(() => {
    const raw =
      row?.vehicle_system_details ?? row?.allData?.vehicle_system_details;
    if (Array.isArray(raw)) {
      return raw;
    }
    if (raw) {
      return [raw];
    }
    return [];
  }, [row?.vehicle_system_details, row?.allData?.vehicle_system_details]);

  const [selectedVsId, setSelectedVsId] = useState(
    vsList.length > 0 ? vsList[0]?.id : "",
  );

  useEffect(() => {
    if (
      vsList.length > 0 &&
      !vsList.some((vs) => vs?.id === selectedVsId)
    ) {
      setSelectedVsId(vsList[0]?.id);
    }
  }, [vsList, selectedVsId]);

  const handleVsChange = (e) => {
    setSelectedVsId(e.target.value);
  };

  if (vsList.length === 0) {
    return (
      <Box sx={{ textAlign: "left", fontSize: "14px", color: "gray", py: 1 }}>
        No Data Available
      </Box>
    );
  }

  return (
    <Box
      sx={{
        textAlign: "left",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        py: 1,
        ...componentStyle.font.tableText,
      }}
    >
      <Box sx={{ width: "100%", borderRadius: "0px" }}>
        <FormControl
          sx={{
            ...componentStyle.form.select.formControl,
            width: "160px",
          }}
        >
          <Select
            size="small"
            value={selectedVsId || (vsList.length > 0 ? vsList[0]?.id : "")}
            onChange={handleVsChange}
            displayEmpty
            fullWidth
            sx={{ ...componentStyle.form.select.select }}
            MenuProps={{
              PaperProps: {
                style: { width: "160px" },
                sx: { maxHeight: 300 },
              },
            }}
            renderValue={() => "Vehicle Systems"}
          >
            {vsList.map((vs, idx) => (
              <MenuItem
                sx={{ ...componentStyle.form.select.menuItem }}
                key={`vs-${vs?.id || idx}`}
                value={vs?.id}
              >
                <Box
                  sx={{
                    p: 0,
                    m: 0,
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Tooltip
                    title={formatSupplierName?.(
                      vs?.vs_supplier ??
                        vs?.supplier_name ??
                        vs?.supplier ??
                        vs?.intr_supplier,
                    ) || "-"}
                    placement="top"
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        ...componentStyle.font.tableText,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box component="span" fontWeight="bold">
                        Supplier Name:
                      </Box>{" "}
                      {formatSupplierName?.(
                        vs?.vs_supplier ??
                          vs?.supplier_name ??
                          vs?.supplier ??
                          vs?.intr_supplier,
                      ) || "-"}
                    </Typography>
                  </Tooltip>
                  {vs?.assembly_number && (
                    <Tooltip
                      title={vs.assembly_number}
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Assembly No:
                        </Box>{" "}
                        {vs.assembly_number}
                      </Typography>
                    </Tooltip>
                  )}
                  <Tooltip title={vs?.name} placement="top">
                    <Typography
                      variant="body2"
                      sx={{
                        ...componentStyle.font.tableText,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box component="span" fontWeight="bold">
                        Name:
                      </Box>{" "}
                      {vs?.name || "-"}
                    </Typography>
                  </Tooltip>
                  <Tooltip
                    title={vs?.version}
                    placement="top"
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        ...componentStyle.font.tableText,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box component="span" fontWeight="bold">
                        Version:
                      </Box>{" "}
                      {vs?.version || "-"}
                    </Typography>
                  </Tooltip>
                  <Tooltip
                    title={vs?.description}
                    placement="top"
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        ...componentStyle.font.tableText,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                      }}
                    >
                      <Box component="span" fontWeight="bold">
                        Description:
                      </Box>{" "}
                      {vs?.description || "-"}
                    </Typography>
                  </Tooltip>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {!supplier && vsList.length > 0 && (
        <Box sx={{ textAlign: "left", mt: 0.5 }}>
          <CustomButtonV2
            handleOnClick={() => {
              onViewMore?.(row?.allData || row, selectedVsId);
            }}
            size="small"
            btnName={"View More..."}
          />
        </Box>
      )}
    </Box>
  );
}

function VehicleComponentsCell({ row, supplier, onViewMore, formatSupplierName }) {
  const vsList = React.useMemo(() => {
    const raw =
      row?.vehicle_system_details ?? row?.allData?.vehicle_system_details;
    if (Array.isArray(raw)) {
      return raw;
    }
    if (raw) {
      return [raw];
    }
    return [];
  }, [row?.vehicle_system_details, row?.allData?.vehicle_system_details]);

  const allVcList = React.useMemo(() => {
    const raw =
      row?.vehicle_component_details ?? row?.allData?.vehicle_component_details;
    if (Array.isArray(raw)) {
      return raw;
    }
    if (raw) {
      return [raw];
    }
    return [];
  }, [row?.vehicle_component_details, row?.allData?.vehicle_component_details]);

  const [selectedVsId, setSelectedVsId] = useState(
    vsList.length > 0 ? vsList[0]?.id : "",
  );

  useEffect(() => {
    if (
      vsList.length > 0 &&
      !vsList.some((vs) => vs?.id === selectedVsId)
    ) {
      setSelectedVsId(vsList[0]?.id);
    }
  }, [vsList, selectedVsId]);

  const filteredVCs = React.useMemo(() => {
    if (!selectedVsId) return allVcList;
    return allVcList.filter((vc) => {
      const vcVsId = vc?.vs_id ?? vc?.pr_vs_id ?? vc?.vehicle_system_id;
      if (vcVsId != null) {
        return Number(vcVsId) === Number(selectedVsId);
      }
      return vsList.length <= 1;
    });
  }, [allVcList, selectedVsId, vsList.length]);

  const [selectedVcId, setSelectedVcId] = useState(
    filteredVCs.length > 0 ? filteredVCs[0]?.id : "",
  );

  useEffect(() => {
    if (filteredVCs.length > 0) {
      if (!filteredVCs.some((vc) => vc?.id === selectedVcId)) {
        setSelectedVcId(filteredVCs[0]?.id);
      }
    } else {
      setSelectedVcId("");
    }
  }, [filteredVCs, selectedVcId]);

  const handleVsChange = (e) => {
    const newVsId = e.target.value;
    setSelectedVsId(newVsId);
    const nextVCs = allVcList.filter((vc) => {
      const vcVsId = vc?.vs_id ?? vc?.pr_vs_id ?? vc?.vehicle_system_id;
      if (vcVsId != null) {
        return Number(vcVsId) === Number(newVsId);
      }
      return vsList.length <= 1;
    });
    setSelectedVcId(nextVCs.length > 0 ? nextVCs[0]?.id : "");
  };

  const handleVcChange = (e) => {
    setSelectedVcId(e.target.value);
  };

  if (vsList.length === 0 && allVcList.length === 0) {
    return (
      <Box sx={{ textAlign: "left", fontSize: "14px", color: "gray", py: 1 }}>
        No Data Available
      </Box>
    );
  }

  return (
    <Box
      sx={{
        textAlign: "left",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        py: 1,
        ...componentStyle.font.tableText,
      }}
    >
      {vsList.length > 0 && (
        <Box sx={{ width: "100%", borderRadius: "0px" }}>
          <FormControl
            sx={{
              ...componentStyle.form.select.formControl,
              width: "160px",
            }}
          >
            <Select
              size="small"
              value={selectedVsId || vsList[0]?.id}
              onChange={handleVsChange}
              displayEmpty
              fullWidth
              sx={{ ...componentStyle.form.select.select }}
              MenuProps={{
                PaperProps: {
                  style: { width: "160px" },
                  sx: { maxHeight: 300 },
                },
              }}
              renderValue={() => "Vehicle Systems"}
            >
              {vsList.map((vs, idx) => (
                <MenuItem
                  sx={{ ...componentStyle.form.select.menuItem }}
                  key={`vs-comp-${vs?.id || idx}`}
                  value={vs?.id}
                >
                  <Box
                    sx={{
                      p: 0,
                      m: 0,
                      width: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <Tooltip
                      title={formatSupplierName?.(
                        vs?.vs_supplier ??
                          vs?.supplier_name ??
                          vs?.supplier ??
                          vs?.intr_supplier,
                      ) || "-"}
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Supplier Name:
                        </Box>{" "}
                        {formatSupplierName?.(
                          vs?.vs_supplier ??
                            vs?.supplier_name ??
                            vs?.supplier ??
                            vs?.intr_supplier,
                        ) || "-"}
                      </Typography>
                    </Tooltip>
                    {vs?.assembly_number && (
                      <Tooltip
                        title={vs.assembly_number}
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            ...componentStyle.font.tableText,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Box component="span" fontWeight="bold">
                            Assembly No:
                          </Box>{" "}
                          {vs.assembly_number}
                        </Typography>
                      </Tooltip>
                    )}
                    <Tooltip title={vs?.name} placement="top">
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Name:
                        </Box>{" "}
                        {vs?.name || "-"}
                      </Typography>
                    </Tooltip>
                    <Tooltip
                      title={vs?.version}
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Version:
                        </Box>{" "}
                        {vs?.version || "-"}
                      </Typography>
                    </Tooltip>
                    <Tooltip
                      title={vs?.description}
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Description:
                        </Box>{" "}
                        {vs?.description || "-"}
                      </Typography>
                    </Tooltip>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {filteredVCs.length > 0 && (
        <Box sx={{ width: "100%", borderRadius: "0px" }}>
          <FormControl
            sx={{
              ...componentStyle.form.select.formControl,
              width: "160px",
            }}
          >
            <Select
              size="small"
              value={selectedVcId || filteredVCs[0]?.id}
              onChange={handleVcChange}
              displayEmpty
              fullWidth
              sx={{ ...componentStyle.form.select.select }}
              MenuProps={{
                PaperProps: {
                  style: { width: "160px" },
                  sx: { maxHeight: 300 },
                },
              }}
              renderValue={() => "Vehicle Components"}
            >
              {filteredVCs.map((vc, idx) => (
                <MenuItem
                  sx={{ ...componentStyle.form.select.menuItem }}
                  key={`vc-${vc?.id || idx}`}
                  value={vc?.id}
                >
                  <Box
                    sx={{
                      p: 0,
                      m: 0,
                      width: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <Tooltip
                      title={formatSupplierName?.(
                        vc?.vc_supplier ??
                          vc?.intr_supplier ??
                          vc?.supplier_name ??
                          vc?.supplier,
                      ) || "-"}
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Supplier Name:
                        </Box>{" "}
                        {formatSupplierName?.(
                          vc?.vc_supplier ??
                            vc?.intr_supplier ??
                            vc?.supplier_name ??
                            vc?.supplier,
                        ) || "-"}
                      </Typography>
                    </Tooltip>
                    {vc?.component_number && (
                      <Tooltip
                        title={vc.component_number}
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            ...componentStyle.font.tableText,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Box component="span" fontWeight="bold">
                            Component No:
                          </Box>{" "}
                          {vc.component_number}
                        </Typography>
                      </Tooltip>
                    )}
                    <Tooltip title={vc?.name} placement="top">
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Name:
                        </Box>{" "}
                        {vc?.name || "-"}
                      </Typography>
                    </Tooltip>
                    <Tooltip
                      title={vc?.version}
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Version:
                        </Box>{" "}
                        {vc?.version || "-"}
                      </Typography>
                    </Tooltip>
                    <Tooltip
                      title={vc?.description}
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          ...componentStyle.font.tableText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                        }}
                      >
                        <Box component="span" fontWeight="bold">
                          Description:
                        </Box>{" "}
                        {vc?.description || "-"}
                      </Typography>
                    </Tooltip>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {!supplier && filteredVCs.length > 0 && (
        <Box sx={{ textAlign: "left", mt: 0.5 }}>
          <CustomButtonV2
            handleOnClick={() => {
              onViewMore?.(row?.allData || row, selectedVcId, selectedVsId);
            }}
            size="small"
            btnName={"View More..."}
          />
        </Box>
      )}
    </Box>
  );
}


const isComponentActive = (comp) => {
  if (!comp || typeof comp !== "object") return true;
  if (
    comp.archive_status &&
    String(comp.archive_status).toUpperCase() === "ARCHIVED"
  ) {
    return false;
  }
  if (
    comp.status &&
    (String(comp.status).toUpperCase() === "ARCHIVED" ||
      String(comp.status).toUpperCase() === "INACTIVE")
  ) {
    return false;
  }
  if (comp.is_active === false || comp.is_active === 0) {
    return false;
  }
  return true;
};

const areAllTicketComponentsActive = (ticketRow) => {
  if (!ticketRow) return true;
  const getItems = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === "object") return [val];
    return [];
  };

  const allComponents = [
    ...getItems(
      ticketRow.vehicle_system_details ??
        ticketRow.allData?.vehicle_system_details
    ),
    ...getItems(
      ticketRow.vehicle_component_details ??
        ticketRow.allData?.vehicle_component_details
    ),
    ...getItems(ticketRow.ee_details ?? ticketRow.allData?.ee_details),
    ...getItems(ticketRow.swm_details ?? ticketRow.allData?.swm_details),
  ];

  if (allComponents.length === 0) return true;
  return allComponents.every(isComponentActive);
};

function TicketsTable({
  tickets_type = TICKETS_TYPES.EE,
  ticketDetails,
  hasTicketNeedMore,
  setSelectedTicketType,
  fetchTickets,
  selectedIntersectionIds,
  setSelectedIntersectionIds,
  enableSelect = false,
  enableAction = false,
  showPagination = false,
  totalCount = 0,
  enableSimulation = false,
  getFilterDetails,
  parentPage,
}) {
  const ticketStatusNames = {
    101: "Draft",
    100: "New",
    150: "Confirm Legal Requirements",
    200: "Artifact Confirmation",
    300: "Evidence Submission",
    400: "Evidence Review",
    500: "Testing at TA reviewers",
    600: "Resolved",
    999: "Closed Incomplete",
  };
  const ticketStatusMapping = {
    DRAFT: 101,
    NEW: 100,
    CONFIRM_LEGAL_REQUIREMENTS: 150,
    ARTIFACT_CONFIRMATION: 200,
    EVIDENCE_SUBMISSION: 300,
    EVIDENCE_REVIEW: 400,
    TESTING_AT_TS: 500,
    RESOLVED: 600,
    CANCELLED: 999,
  };

  const formatSupplierName = (supplier) => {
    if (Array.isArray(supplier)) {
      return supplier.filter(Boolean).join(", ") || "-";
    }
    return supplier || "-";
  };

  const LinkButton = styled(Button)(({ theme }) => ({
    textTransform: "none",
    color: theme.palette.primary.main,
    textDecoration: "underline",
    padding: 0,
    minWidth: "auto",
    "&:hover": {
      textDecoration: "underline",
      backgroundColor: "transparent",
    },
  }));

  const CountdownTimer = styled(Typography)(({ theme }) => ({
    backgroundColor: "green",
    color: "white",
    borderRadius: 0,
    textAlign: "center",
    width: "100px",
    cursor: "pointer",
  }));
  const dispatch = useDispatch();

  const imagePathDetails = useSelector((state) => state?.profileReducer);
  const selectedProject = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariant = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  const profileDetails = useSelector((store) => store.profileReducer);
  const navigate = useNavigate();

  const [selectedComponent, setSelectedComponent] = useState(null);
  const [selectedComponentFromGetApi, setSelectedComponentFromGetApi] =
    useState();
  const [linkApiProps, setLinkApiProps] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [currentTicketId, setCurrentTicketId] = useState(null);
  const [open, setOpen] = useState(false);
  const [timer, setTimer] = useState(5);
  const [intervalId, setIntervalId] = useState(null);
  const [userDetailsMap, setUserDetailsMap] = useState(new Map());
  const [legalDialogData, setLegalDialogData] = useState(null);
  const [isLegalDialogOpen, setIsLegalDialogOpen] = useState(false);
  const [selectedTableRowData, setSelectedTableRowData] = useState(null);

  const [dynamicFormData, setDynamicFormData] = useState(null);
  const [isEEDialogOpen, setIsEEDialogOpen] = useState(false);
  const [tableRowsData, setTableRowsData] = useState([]);
  const [apiAction, setApiAction] = useState("");
  const [apiDataKey, setApiDataKey] = useState("");
  const [moduleViewMoreType, setModuleViewMoreType] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const fileInputRef = useRef(null);
  const [currentParams, setCurrentParams] = useState(null);
  const [openModal, setOpenModal] = React.useState(false);
  const [imagePath, setImagePath] = React.useState("");
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [confirmArchiveRetrieveDialogOpen, setConfirmArchiveRetrieveDialogOpen] =
    useState(false);
  const [confirmActionType, setConfirmActionType] = useState("");
  const [disableAction, setDisableAction] = useState(false);
  const [externalUrl, setExternalUrl] = useState(null);
  const [externalSource, setExternalSource] = useState(null);
  const roleType = profileDetails?.profile?.role_details?.role_type;
  const supplier = roleType === 2;

  const handleConfirmArchiveRetrieve = async () => {
    if (!selectedTableRowData?.intr_id || !selectedProject?.project_id || !selectedVariant?.variant_id) {
      setConfirmArchiveRetrieveDialogOpen(false);
      return;
    }

    const api =
      confirmActionType === "archive"
        ? typeApprovalAPI.archive_intersection
        : typeApprovalAPI.retrieve_intersection;

    try {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...api,
            params: {
              project_id: selectedProject?.project_id,
              variant_id: selectedVariant?.variant_id,
              intr_id: [selectedTableRowData?.intr_id],
            },
            show_toast: true,
          }),
        ),
      );
      if (res) {
        if (fetchTickets) fetchTickets();
      }
    } catch (err) {
      console.error(`Error performing ${confirmActionType}:`, err);
    } finally {
      setConfirmArchiveRetrieveDialogOpen(false);
    }
  };

  const getTabBasedOnTicketType = () => {
    switch (Number(tickets_type)) {
      case 6: // Vehicle System
        return 0;
      case 3: // Vehicle Component
        return 1;
      case 2: // EE
        return 2;
      case 1: // SWM
        return 3;
      default:
        return 0;
    }
  };

  const getComponentTypeByTicketType = (ticketType = tickets_type) => {
    switch (Number(ticketType)) {
      case 1:
        return "swm";
      case 2:
        return "ee";
      case 3:
        return "vc";
      case 6:
        return "vs";
      default:
        return "ee";
    }
  };

  const getSelectedComponentApiByTicketType = (rowData) => {
    const ticketType = Number(rowData?.tickets_type ?? tickets_type);
    const projectId = Number(rowData?.project_details?.project_id);
    const variantId = Number(rowData?.variant_details?.[0]?.variant_id);
    const baseParams = {
      project_id: projectId,
      variant_id: variantId,
      page_number: 1,
      page_size: 100,
    };

    const getFirst = (data) => (Array.isArray(data) ? data[0] : data);
    const ee = getFirst(rowData?.ee_details);
    const swm = getFirst(rowData?.swm_details);
    const vs = getFirst(rowData?.vehicle_system_details);
    const vc = getFirst(rowData?.vehicle_component_details);

    if (ticketType === 2) {
      return {
        apiAction: labCar.get_ee_components,
        params: {
          ...baseParams,
          ...(ee?.ee_id != null && { ee_id: Number(ee.ee_id) }),
        },
        matchFn: (item) => Number(item?.ee_id) === Number(ee?.ee_id),
      };
    }

    if (ticketType === 1) {
      return {
        apiAction: labCar.get_sw_modules,
        params: {
          ...baseParams,
          ...(ee?.ee_id != null && { ee_id: Number(ee.ee_id) }),
          ...(swm?.swm_id != null && { swm_id: Number(swm.swm_id) }),
        },
        matchFn: (item) => Number(item?.swm_id) === Number(swm?.swm_id),
      };
    }

    if (ticketType === 6) {
      return {
        apiAction: labCar.get_vehicle_systems,
        params: {
          ...baseParams,
          ...(vs?.id != null && { vs_id: Number(vs.id) }),
        },
        matchFn: (item) => Number(item?.id ?? item?.vs_id) === Number(vs?.id),
      };
    }

    if (ticketType === 3) {
      return {
        apiAction: labCar.get_vehicle_components,
        params: {
          ...baseParams,
          ...(vs?.id != null && { vs_id: Number(vs.id) }),
          ...(vc?.id != null && { vc_id: Number(vc.id) }),
        },
        matchFn: (item) => Number(item?.id ?? item?.vc_id) === Number(vc?.id),
      };
    }

    return null;
  };

  const handleClickOpen = (ticket_id, url, source = "SNOW") => {
    console.log("---handleClickOpen---", source);
    switch (source) {
      case "SNOW":
        setExternalSource("ServiceNow");
        break;
      case "JIRA":
        setExternalSource("Jira");
        break;
      default:
        setExternalSource("ServiceNow");
    }

    setCurrentTicketId(ticket_id);
    setExternalUrl(url || null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    if (intervalId) {
      clearInterval(intervalId);
    }
  };

  const handleMoreClick = (e, item) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedTableRowData(item); // Store the selected row data
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedTableRowData(null); // Clear the selected row data
  };

  const handleSimulationClick = (event, params) => {
    event.preventDefault();
    setCurrentParams(params);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
    handleCloseMenu();
  };

  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      console.log("Selected files:", files);
      void handleImageChange(event, currentParams);
    }
  };

  const handleImageRowClick = ({ row }) => {
    console.log("View simulation file:", row);
    setOpenModal(true);
    dispatch(
      HandleApiActions({
        ...profileActions.get_profile_pic,
        show_toast: true,
        file_name: row?.simulation_file,
        is_file: true,
      }),
    );
  };

  const handleRedirect = () => {
    setOpen(false);
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    if (currentTicketId) {
      const linkType = currentTicketId.startsWith("INC")
        ? "incident.do"
        : "sn_customerservice_case.do";
      let url =
        externalUrl ||
        `https://ven07074.service-now.com/${linkType}?sysparm_query=number=${currentTicketId}`;
      window.open(url, "_blank");
    }
  };


  const handleImageChange = async (event, params) => {
    console.log("parrrwfwsfs", params);
    const file = event.target.files[0];
    setDisableAction(true);
    if (file) {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const allowedExtensions = ["jpg", "jpeg", "png"];
      if (allowedExtensions.includes(fileExtension)) {
        const resp = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...profileActions.upload_profile_pic,
              show_toast: true,
              file: file,
              is_file: true,
              ignore_app_loading_spinner: true,
            }),
          ),
        );

        await Promise.resolve(
          dispatch(
            HandleApiActions({
              api_name: "/ACM/intersections/upload/simulation-file",
              https_method: "PUT",
              params: {
                intr_id: params?.row?.intr_id,
                file_name: resp?.file_name,
              },
              show_toast: true,
              ignore_app_loading_spinner: true,
            }),
          ),
        );

        await fetchTickets();
        setDisableAction(false);
      } else {
        toast.dark("Please select a valid image file", {
          type: "error",
          autoClose: 3000,
        });
      }
    }
  };

  useEffect(() => {
    setImagePath(imagePathDetails?.profile_pic_path);
  }, [imagePathDetails]);

  const handleSWMViewButtonClick = (item) => {
    const swm = Array.isArray(item?.swm_details) ? item?.swm_details[0] : item?.swm_details;
    const ee = Array.isArray(item?.ee_details) ? item?.ee_details[0] : item?.ee_details;
    setSelectedComponent(item);
    setApiAction(softwareIntersectionAPI.get_software_intersections);
    setLinkApiProps({
      swm_id: swm?.swm_id,
      ee_id: ee?.ee_id,
    });
    setApiDataKey("linked_swm_details");
    setModuleViewMoreType("SWM");

    toggleEEDialog(true)();
  };

  const handleEEViewButtonClick = (item) => {
    const ee = Array.isArray(item?.ee_details) ? item?.ee_details[0] : item?.ee_details;

    setSelectedComponent(item);
    setApiAction(labCar.get_ee_intersections);
    setLinkApiProps({
      ee_id: ee?.ee_id,
    });
    setApiDataKey("linked_ee_details");
    setModuleViewMoreType("EE");

    toggleEEDialog(true)();
  };

  const handleVSViewButtonClick = (item) => {
    console.log("---handleVSViewButtonClick---", item);
    const vs = Array.isArray(item?.vehicle_system_details) ? item?.vehicle_system_details[0] : item?.vehicle_system_details;

    setSelectedComponent(item);
    setApiAction(labCar.get_vehicle_systems);
    setLinkApiProps({
      vs_id: vs?.id,
    });
    setSelectedComponent(item);
    setApiDataKey("linked_vs_details");
    setModuleViewMoreType("VS");

    toggleEEDialog(true)();
  };

  const handleVCViewButtonClick = (item, customVcId = null, customVsId = null) => {
    console.log("---handleVCViewButtonClick---", item, customVcId, customVsId);
    const rawVc = item?.vehicle_component_details;
    let vcList = [];
    if (Array.isArray(rawVc)) {
      vcList = rawVc;
    } else if (rawVc) {
      vcList = [rawVc];
    }

    const rawVs = item?.vehicle_system_details;
    let vsList = [];
    if (Array.isArray(rawVs)) {
      vsList = rawVs;
    } else if (rawVs) {
      vsList = [rawVs];
    }

    const vc = customVcId
      ? vcList.find((v) => Number(v?.id) === Number(customVcId)) || { id: customVcId }
      : vcList[0];
    const vs = customVsId
      ? vsList.find((v) => Number(v?.id) === Number(customVsId)) || { id: customVsId }
      : vsList[0];

    setSelectedComponent(item);
    setApiAction(labCar.get_vehicle_components);
    setLinkApiProps({
      vc_id: vc?.id,
      vs_id: vs?.id,
    });
    setApiDataKey("linked_vc_details");
    setModuleViewMoreType("VC");

    toggleEEDialog(true)();
  };

  const toggleLegalDialog = (newOpen) => () => {
    setIsLegalDialogOpen(newOpen);
  };
  const handleLEGALViewButtonClick = (item) => {
    const rawLr = item?.lr_details;
    let lr_details = [];
    if (Array.isArray(rawLr)) {
      lr_details = rawLr;
    } else if (rawLr) {
      lr_details = [rawLr];
    }

    const rawLs = item?.ls_details;
    let ls_details = [];
    if (Array.isArray(rawLs)) {
      ls_details = rawLs;
    } else if (rawLs) {
      ls_details = [rawLs];
    }

    const items2 = lr_details.map(lr => ({
      lr_id: lr.lr_id,
      chapter: lr.chapter,
      description: lr.description,
      req_number: lr.req_number,
    }));

    const items3 = ls_details.map(ls => ({
      ls_id: ls.ls_id,
      standard_number: ls.standard_number,
      name: ls.name,
      revision: ls.revision,
      description: ls.description,
      chapters: ls.chapters,
    }));

    setLegalDialogData({
      heading2: `Legal Requirement`,
      items2: items2,
      items3: items3, // Optionally include ls_details if needed
    });

    toggleLegalDialog(true)();
  };


  const toggleEEDialog = (newOpen) => () => {
    setIsEEDialogOpen(newOpen);
  };

  const fetchUserDetails = async () => {
    try {
      const apiDetails = userManagementApis?.get_users_list_pa;
      delete apiDetails.action_type;
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              page_size: 10000,
              page_number: 1,
            },
            show_toast: false,
          }),
        ),
      );

      if (!resp.error) {
        const users = resp?.data || [];
        const userMap = new Map(users?.map((user) => [user?.user_id, user]));
        setUserDetailsMap(userMap);
      }
    } catch (error) {
      console.error("Failed to fetch user details:", error);
    }
  };

  useEffect(() => {
    if (!open) return; // Prevent interval from creating when dialog is closed.

    setTimer(5); // Reset timer here so it’s set BEFORE interval starts.

    const id = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          handleRedirect();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setSelectedTicketType(tickets_type);
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleIntersectionChange = (checked, intrId) => {
    setSelectedIntersectionIds((prev) => {
      if (checked) {
        return [...prev, intrId];
      } else {
        return prev.filter((id) => id !== intrId);
      }
    });
  };
  const COLUMNS = [
    ...(enableSelect
      ? [
        {
          field: "select",
          headerName: "",
          headerAlign: "center",
          width: 50,
          sortable: false,
          disableColumnMenu: true,
          renderCell: (params) => (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                pl: 2,
                width: "100%",
              }}
            >
              {params.row.status === ticketStatusMapping.DRAFT ? (
                <CustomCheckBoxV2
                  value={selectedIntersectionIds.includes(
                    params?.row?.intr_id,
                  )}
                  onChange={(e) =>
                    handleIntersectionChange(
                      e.target.checked,
                      params?.row?.intr_id,
                    )
                  }
                />
              ) : (
                <></>
              )}
            </Box>
          ),
        },
      ]
      : []),
    {
      field: "ticket_id",
      headerName: "Ticket Details",
      headerAlign: "center",
      minWidth: 180,
      flex: 1,
      renderCell: (params) => {
        console.log("---Ticket Details---", params?.row);
        const renderDetails = (label, details) => (
          <Box
            sx={{
              marginBottom: "8px",
              p: 0,
              height: "100%",
            }}
          >
            <Box sx={{ fontWeight: "bold", mb: 1 }}>{label}</Box>
            {Object.entries(details).map(([key, value]) => (
              <Box
                key={key}
                sx={{
                  ...componentStyle.font.tableText,
                  display: "flex",
                  gap: 0.5,
                }}
              >
                {key.replaceAll("_", " ")}:{" "}
                <CustomToolTip
                  length={12}
                  style={{ ...componentStyle.font.tableText }}
                >
                  {value || "-"}
                </CustomToolTip>
              </Box>
            ))}
          </Box>
        );

        return (
          <Box
            sx={{
              textAlign: "left",
              ...componentStyle.font.tableText,

              p: 0,
              m: "-8px 0",
              width: "100%",
            }}
          >
            {renderDetails("", {
              "Ticket Id": params?.value ? (
                <LinkButton
                  sx={{ ...componentStyle.font.tableText, mt: -0.5 }}
                  onClick={() =>
                    handleClickOpen(
                      params.value,
                      params.row.external_url || null,
                      params.row.source_type || "SNOW",
                    )
                  }
                >
                  {params?.value}
                </LinkButton>
              ) : (
                "-"
              ),
              "Intersection Id": params?.row?.intr_id || "-",
              Status:
                params?.row?.archive_status?.toUpperCase() === "ARCHIVED"
                  ? "Archived"
                  : ticketStatusNames[params?.row?.status],
              "Created By":
                userDetailsMap?.get(params?.row?.created_by)?.fullname || "-",
              "Updated By": params?.row?.updated_user_details?.[0]?.name,
              Description: params?.row?.description,
              Reason: params?.row?.reason,
            })}

            <CustomDialogBoxV2
              open={open}
              onClose={handleClose}
              title={<Box></Box>}
            >
              <Box
                sx={{
                  width: "400px",
                  p: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ ...componentStyle.font.tabLevelHeader }}
                >
                  Redirecting to {externalSource} Ticketing tool!
                </Typography>

                <CountdownTimer
                  onClick={handleRedirect}
                  sx={{
                    width: "fit-content",
                    mt: 1,
                    px: 2,
                    ...componentStyle.font.tabLevelHeader,
                    fontWeight: 500,
                  }}
                >
                  Redirecting in ({timer}) sec..
                </CountdownTimer>
              </Box>
            </CustomDialogBoxV2>
          </Box>
        );
      },
    },
    ...(Number(tickets_type) === TICKETS_TYPES.VEHICLE_SYSTEM
      ? [
          {
            field: "vehicle_system_details",
            headerName: "Vehicle System",
            headerAlign: "left",
            width: 180,
            renderCell: (params) => (
              <VehicleSystemCell
                row={params?.row}
                supplier={supplier}
                onViewMore={handleVSViewButtonClick}
                formatSupplierName={formatSupplierName}
              />
            ),
          },
          {
            field: "vehicle_component_details",
            headerName: "Vehicle Components",
            headerAlign: "left",
            width: 180,
            renderCell: (params) => (
              <VehicleComponentsCell
                row={params?.row}
                supplier={supplier}
                onViewMore={handleVCViewButtonClick}
                formatSupplierName={formatSupplierName}
              />
            ),
          },
        ]
      : [
          {
            field: "tickets_type",
            headerName: "Module Type",
            headerAlign: "left",
            minWidth: 180,
            flex: 1,
            renderCell: (params) => {
              const swm_details = params?.row?.swm_details;
              const ee_details = params?.row?.ee_details;
              const vehicle_component_details = params?.row?.vehicle_component_details;

              const renderModuleCellContent = () => {
                const isSwm =
                  Array.isArray(swm_details) &&
                  swm_details.length > 0 &&
                  Number(params?.row?.ticket_type) === 1;

                if (isSwm) {
                  return (
                    <Box sx={{ width: "100%", borderRadius: "0px" }}>
                      <Box sx={{ width: "100%" }}>
                        <FormControl
                          sx={{
                            ...componentStyle.form.select.formControl,
                            width: "100%",
                          }}
                        >
                          <Select
                            size="small"
                            value={swm_details[0]?.swm_id}
                            displayEmpty
                            fullWidth
                            sx={{ ...componentStyle.form.select.select }}
                            MenuProps={{
                              PaperProps: {
                                style: { width: "160px" },
                                sx: { maxHeight: 300 },
                              },
                            }}
                            renderValue={(selected) => "Software Modules"}
                          >
                            {swm_details.map((component) => (
                              <MenuItem
                                sx={{ ...componentStyle.form.select.menuItem }}
                                key={`swm-${component.swm_id || component.name}`}
                                value={component.swm_id}
                              >
                                <Box
                                  sx={{
                                    p: 0,
                                    m: 0,
                                    width: "100%",
                                    overflow: "hidden",
                                  }}
                                >
                                  <Tooltip
                                    title={formatSupplierName(
                                      component?.swm_supplier ??
                                        component?.intr_supplier ??
                                        component?.supplier_name ??
                                        component?.supplier,
                                    )}
                                    placement="top"
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        ...componentStyle.font.tableText,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <Box component="span" fontWeight="bold">
                                        Supplier Name:
                                      </Box>{" "}
                                      {formatSupplierName(
                                        component?.swm_supplier ??
                                          component?.intr_supplier ??
                                          component?.supplier_name ??
                                          component?.supplier,
                                      )}
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip title={component.name} placement="top">
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        ...componentStyle.font.tableText,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <Box component="span" fontWeight="bold">
                                        Number:
                                      </Box>{" "}
                                      {component.name || "-"}
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip
                                    title={component.version}
                                    placement="top"
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        ...componentStyle.font.tableText,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <Box component="span" fontWeight="bold">
                                        Version:
                                      </Box>{" "}
                                      {component.version || "-"}
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip
                                    title={component.description}
                                    placement="top"
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        ...componentStyle.font.tableText,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      <Box component="span" fontWeight="bold">
                                        Description:
                                      </Box>{" "}
                                      {component.description || "-"}
                                    </Typography>
                                  </Tooltip>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      {!supplier && (
                        <Box sx={{ textAlign: "left", marginTop: 2 }}>
                          <CustomButtonV2
                            handleOnClick={() => {
                              handleSWMViewButtonClick(params?.row?.allData);
                            }}
                            size="small"
                            btnName={"View More..."}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                }

                const isEe =
                  Array.isArray(ee_details) &&
                  ee_details.length > 0 &&
                  Number(params?.row?.ticket_type) === 2;

                if (isEe) {
                  return (
                    <Box sx={{ width: "100%", borderRadius: "0px" }}>
                      <Box sx={{ width: "100%" }}>
                        <FormControl
                          sx={{
                            ...componentStyle.form.select.formControl,
                            width: "100%",
                          }}
                        >
                          <Select
                            size="small"
                            value={ee_details[0]?.ee_id}
                            displayEmpty
                            fullWidth
                            sx={{ ...componentStyle.form.select.select }}
                            MenuProps={{
                              PaperProps: {
                                style: { width: "160px" },
                                sx: { maxHeight: 300 },
                              },
                            }}
                            renderValue={(selected) => "EE Components"}
                          >
                            {ee_details.map((component) => (
                              <MenuItem
                                sx={{ ...componentStyle.form.select.menuItem }}
                                key={`ee-${component.ee_id || component.assembly_number}`}
                                value={component.ee_id}
                              >
                                <Box
                                  sx={{
                                    p: 0,
                                    m: 0,
                                    width: "100%",
                                    overflow: "hidden",
                                  }}
                                >
                                  <Tooltip
                                    title={formatSupplierName(
                                      component?.intr_supplier ??
                                        component?.supplier_name ??
                                        component?.supplier,
                                    )}
                                    placement="top"
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        ...componentStyle.font.tableText,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <Box component="span" fontWeight="bold">
                                        Supplier Name:
                                      </Box>{" "}
                                      {formatSupplierName(
                                        component?.intr_supplier ??
                                          component?.supplier_name ??
                                          component?.supplier,
                                      )}
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip
                                    title={component.assembly_number}
                                    placement="top"
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        ...componentStyle.font.tableText,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <Box component="span" fontWeight="bold">
                                        Assembly No:
                                      </Box>{" "}
                                      {component.assembly_number || "-"}
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip
                                    title={component.version}
                                    placement="top"
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        ...componentStyle.font.tableText,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <Box component="span" fontWeight="bold">
                                        Version:
                                      </Box>{" "}
                                      {component.version || "-"}
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip
                                    title={component.description}
                                    placement="top"
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        ...componentStyle.font.tableText,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      <Box component="span" fontWeight="bold">
                                        Description:
                                      </Box>{" "}
                                      {component.description || "-"}
                                    </Typography>
                                  </Tooltip>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      {params?.row?.allData?.dependencies?.ee_components &&
                        !supplier && (
                          <Box sx={{ textAlign: "left", marginTop: 2 }}>
                            <CustomButtonV2
                              handleOnClick={(event) => {
                                handleEEViewButtonClick(params?.row?.allData);
                              }}
                              size="small"
                              btnName={"View More..."}
                            />
                          </Box>
                        )}
                    </Box>
                  );
                }

                const isVc =
                  Array.isArray(vehicle_component_details) &&
                  vehicle_component_details.length > 0;

                if (isVc) {
                  return (
                    <Box sx={{ width: "100%", borderRadius: "0px" }}>
                      <Box sx={{ width: "100%" }}>
                        <FormControl
                          sx={{
                            ...componentStyle.form.select.formControl,
                            width: "100%",
                          }}
                        >
                          <Select
                            size="small"
                            value={vehicle_component_details[0]?.id}
                            displayEmpty
                            fullWidth
                            sx={{ ...componentStyle.form.select.select }}
                            MenuProps={{
                              PaperProps: {
                                style: { width: "160px" },
                                sx: { maxHeight: 300 },
                              },
                            }}
                            renderValue={(selected) => "Vehicle Components"}
                          >
                            {vehicle_component_details.map((component) => (
                              <MenuItem
                                sx={{ ...componentStyle.form.select.menuItem }}
                                key={`vehicle-${component.id}`}
                                value={component.id}
                              >
                                <Box
                                  sx={{
                                    p: 0,
                                    m: 0,
                                    width: "100%",
                                    overflow: "hidden",
                                  }}
                                >
                                  <Tooltip
                                    title={formatSupplierName(
                                      component?.vc_supplier ??
                                        component?.intr_supplier ??
                                        component?.supplier_name ??
                                        component?.supplier,
                                    )}
                                    placement="top"
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        ...componentStyle.font.tableText,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <Box component="span" fontWeight="bold">
                                        Supplier Name:
                                      </Box>{" "}
                                      {formatSupplierName(
                                        component?.vc_supplier ??
                                          component?.intr_supplier ??
                                          component?.supplier_name ??
                                          component?.supplier,
                                      )}
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip
                                    title={component.component_number}
                                    placement="top"
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        ...componentStyle.font.tableText,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <Box component="span" fontWeight="bold">
                                        Component No:
                                      </Box>{" "}
                                      {component.component_number || "-"}
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip title={component.name} placement="top">
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        ...componentStyle.font.tableText,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <Box component="span" fontWeight="bold">
                                        Name:
                                      </Box>{" "}
                                      {component.name || "-"}
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip
                                    title={component.description}
                                    placement="top"
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        ...componentStyle.font.tableText,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      <Box component="span" fontWeight="bold">
                                        Description:
                                      </Box>{" "}
                                      {component.description || "-"}
                                    </Typography>
                                  </Tooltip>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      {!supplier && (
                        <Box sx={{ textAlign: "left", marginTop: 2 }}>
                          <CustomButtonV2
                            handleOnClick={(event) => {
                              handleVCViewButtonClick(params?.row?.allData);
                            }}
                            size="small"
                            btnName={"View More..."}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                }

                return <Box sx={{ color: "gray" }}>No Data Available</Box>;
              };

              return (
                <Box
                  sx={{
                    textAlign: "left",
                    ...componentStyle.font.tableText,
                  }}
                >
                  {renderModuleCellContent()}
                </Box>
              );
            },
          },
        ]),


    {
      field: "ls_lr_details",
      headerName: "Legal Details",
      headerAlign: "left",
      minWidth: 200,
      flex: 1,

      renderCell: (params) => {
        const ls_details = params?.row?.ls_details;
        return (
          <Box sx={{ textAlign: "left", ...componentStyle.font.tableText }}>
            <Box sx={{ height: "90px", minHeight: "90px", overflowY: "auto", padding: "4px", borderRadius: "4px" }}>
              {Array.isArray(ls_details) && ls_details.length > 0 ? (
                ls_details.map((ls, idx) => (
                  <Box key={`ls-${ls?.ls_id || ls?.standard_number || ls?.name || 'std'}`} sx={{ mb: 1, pb: 1, borderBottom: idx < ls_details.length - 1 ? '1px solid #eee' : 'none' }}>
                    <Box><strong>Standard No:</strong> {ls?.standard_number || "-"}</Box>
                    <Box><strong>Name:</strong> {ls?.name || "-"}</Box>
                    <Box><strong>Revision:</strong> {ls?.revision || "-"}</Box>
                  </Box>
                ))
              ) : (
                <Box>
                  <Box><strong>Standard No:</strong> -</Box>
                  <Box><strong>Name:</strong> -</Box>
                  <Box><strong>Revision:</strong> -</Box>
                </Box>
              )}
            </Box>

            <Box sx={{ textAlign: "left", marginTop: 2 }}>
              <CustomButtonV2
                handleOnClick={() => {
                  handleLEGALViewButtonClick(params?.row?.allData);
                }}
                size="small"
                btnName={"View More..."}
              />
            </Box>
          </Box>
        );
      },
    },
    {
      field: "test_case_details",
      headerName: "Test Case Details",
      headerAlign: "left",
      width: 180,
      renderCell: (params) => {
        const testCaseDetails = params?.row?.test_case_details ?? [];
        const hasTestCases = testCaseDetails.length > 0;

        return (
          <Box sx={{ width: "100%", borderRadius: "0px", paddingTop: "40px" }}>
            <Box sx={{ width: "100%" }}>
              <FormControl
                sx={{
                  ...componentStyle.form.select.formControl,
                  width: "160px",
                }}
              >
                <Select
                  size="small"
                  value=""
                  displayEmpty
                  fullWidth
                  renderValue={() =>
                    hasTestCases ? "View Test Cases" : "No Test Cases"
                  }
                  sx={{ ...componentStyle.form.select.select }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        width: "160px",
                      },
                      sx: { maxHeight: 300 },
                    },
                  }}
                >
                  {testCaseDetails.map((testCase) => (
                    <MenuItem
                      key={testCase.manual_test_case_id}
                      value={testCase.manual_test_case_id}
                      sx={{ ...componentStyle.form.select.menuItem }}
                    >
                      <Box
                        sx={{
                          p: 0,
                          m: 0,
                          width: "100%",
                          overflow: "hidden",
                        }}
                      >
                        <Tooltip
                          title={testCase?.manual_test_case_id}
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              ...componentStyle.font.tableText,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Box component="span" fontWeight="bold">
                              ID:
                            </Box>{" "}
                            {testCase?.manual_test_case_id ?? "-"}
                          </Typography>
                        </Tooltip>

                        <Tooltip title={testCase?.name} placement="top">
                          <Typography
                            variant="body2"
                            sx={{
                              ...componentStyle.font.tableText,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Box component="span" fontWeight="bold">
                              Name:
                            </Box>{" "}
                            {testCase?.name ?? "-"}
                          </Typography>
                        </Tooltip>

                        <Tooltip title={testCase?.description} placement="top">
                          <Typography
                            variant="body2"
                            sx={{
                              ...componentStyle.font.tableText,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%", // ensures ellipsis
                            }}
                          >
                            <Box component="span" fontWeight="bold">
                              Description:
                            </Box>{" "}
                            {testCase?.description ?? "-"}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        );
      },
    },

    {
      field: "users",
      headerName: "Assignee",
      headerAlign: "center",
      width: 180,
      renderCell: (params) => {
        const techServiceUsers = params?.row?.tech_service_users || [];
        const oemUsers = params?.row?.oem_users || [];
        const supplierUsers = params?.row?.suppliers || [];
        const defaultUserDetails = {
          fullname: "User removed",
          email: "",
          role_details: {},
        };

        const techServiceUserOptions = techServiceUsers?.map((userId) => ({
          id: userId,
          ...(userDetailsMap?.get(userId) || defaultUserDetails),
        }));

        const oemUserOptions = oemUsers?.map((userId) => ({
          id: userId,
          ...(userDetailsMap?.get(userId) || defaultUserDetails),
        }));

        const supplierUserOptions = supplierUsers?.map((userId) => ({
          id: userId,
          ...(userDetailsMap?.get(userId) || defaultUserDetails),
        }));

        const techServiceDefault =
          techServiceUserOptions?.length > 0
            ? techServiceUserOptions[0]?.id
            : "";
        const oemDefault =
          oemUserOptions?.length > 0 ? oemUserOptions[0]?.id : "";

        const supplierDefault =
          supplierUserOptions?.length > 0 ? supplierUserOptions[0]?.id : "";

        return (
          <FormControl
            sx={{ ...componentStyle.form.select.formControl, width: "160px" }}
          >
              {techServiceUserOptions?.length > 0 && (
                <>
                  <Typography
                    component="span"
                    sx={{ ...componentStyle.form.select.label }}
                  >
                    Select Compliance Reviewers
                  </Typography>
                  <Select
                    value={techServiceDefault}
                    displayEmpty
                    sx={{ ...componentStyle.form.select.select }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          width: "160px",
                        },
                        sx: { maxHeight: 300 },
                      },
                    }}
                    renderValue={(selected) => {
                      const selectedUser = techServiceUserOptions?.find(
                        (user) => user?.id === selected,
                      );
                      return selectedUser
                        ? selectedUser?.fullname
                        : "No Compliance reviewers";
                    }}
                  >
                    {techServiceUserOptions?.map((user) => (
                      <MenuItem
                        sx={{
                          ...componentStyle.form.select.menuItem,
                          "&:hover": {
                            backgroundColor: "#e0e0e0 !important",
                          },

                          "&.Mui-selected": {
                            backgroundColor: "#ebebeb !important",
                          },

                          "&.Mui-selected:hover": {
                            backgroundColor: "#e0e0e0 !important",
                          },
                        }}
                        key={`tech-${user?.id}`}
                        value={user?.id}
                      >
                        <Box
                          sx={{
                            p: 0,
                            m: 0,
                            width: "100%",
                            overflow: "hidden",
                          }}
                        >
                          {user?.fullname}
                          <br />
                          {user?.email}
                          <br />
                          {user?.role_details?.role_name}
                          <br />
                          {user?.role_details?.department}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}

              {oemUserOptions?.length > 0 && (
                <>
                  <Typography
                    component="span"
                    sx={{
                      ...componentStyle.form.select.label,
                      marginTop:
                        techServiceUserOptions?.length > 0 ? "10px" : "0",
                    }}
                  >
                    Select Compliance Requestors
                  </Typography>
                  <Select
                    value={oemDefault}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          width: "160px",
                        },
                        sx: { maxHeight: 300 },
                      },
                    }}
                    displayEmpty
                    sx={{
                      ...componentStyle.form.select.select,
                    }}
                    renderValue={(selected) => {
                      const selectedUser = oemUserOptions?.find(
                        (user) => user?.id === selected,
                      );
                      return selectedUser
                        ? selectedUser?.fullname
                        : "No TA requestors";
                    }}
                  >
                    {oemUserOptions?.map((user) => (
                      <MenuItem
                        sx={{
                          ...componentStyle.form.select.menuItem,
                          "&:hover": {
                            backgroundColor: "#e0e0e0 !important",
                          },

                          "&.Mui-selected": {
                            backgroundColor: "#ebebeb !important",
                          },

                          "&.Mui-selected:hover": {
                            backgroundColor: "#e0e0e0 !important",
                          },
                        }}
                        key={`oem-${user?.id}`}
                        value={user?.id}
                      >
                        {user?.fullname}
                        <br />
                        {user?.email}
                        <br />
                        {user?.role_details?.role_name}
                        <br />
                        {user?.role_details?.department}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}

              {supplierUserOptions?.length > 0 && (
                <>
                  <Typography
                    component="span"
                    sx={{
                      ...componentStyle.form.select.label,
                      marginTop: supplierUserOptions?.length > 0 ? "10px" : "0",
                    }}
                  >
                    Suppliers
                  </Typography>
                  <Select
                    value={supplierDefault}
                    displayEmpty
                    sx={{
                      ...componentStyle.form.select.select,
                    }}
                    renderValue={(selected) => {
                      const selectedUser = supplierUserOptions?.find(
                        (user) => user?.id === selected,
                      );
                      return selectedUser
                        ? selectedUser?.fullname
                        : "No Suppliers";
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          width: "160px",
                        },
                        sx: { maxHeight: 300 },
                      },
                    }}
                  >
                    {supplierUserOptions?.map((user) => (
                      <MenuItem
                        sx={{
                          ...componentStyle.form.select.menuItem,
                          "&:hover": {
                            backgroundColor: "#e0e0e0 !important",
                          },

                          "&.Mui-selected": {
                            backgroundColor: "#ebebeb !important",
                          },

                          "&.Mui-selected:hover": {
                            backgroundColor: "#e0e0e0 !important",
                          },
                        }}
                        key={`sup-${user?.id}`}
                        value={user?.id}
                      >
                        {user?.fullname}
                        <br />
                        {user?.email}
                        <br />
                        {user?.role_details?.role_name}
                        <br />
                        {user?.role_details?.department}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}

              {techServiceUserOptions?.length === 0 &&
                oemUserOptions?.length === 0 &&
                supplierUserOptions?.length === 0 && (
                  <Typography sx={componentStyle.font.tableText}>
                    No Users Available
                  </Typography>
                )}
            </FormControl>
        );
      },
    },

    {
      field: "updated_at",
      headerName: "Changed At",
      headerAlign: "center",
      width: 100,
      renderCell: (params) => (
        <Box sx={{ textAlign: "center", ...componentStyle.font.tableText }}>
          {params?.value || "-"}
        </Box>
      ),
    },
    {
      field: "created_at",
      headerName: "Created At",
      headerAlign: "center",
      width: 100,
      renderCell: (params) => (
        <Box sx={{ textAlign: "center", ...componentStyle.font.tableText }}>
          {params?.value || "-"}
        </Box>
      ),
    },
    ...(enableAction
      ? [
        {
          field: "action",
          headerName: "Action",
          headerAlign: "center",
          minWidth: 30,
          flex: 1,
          sortable: false,
          disableColumnMenu: true,
          renderCell: (params) => (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Tooltip title="More">
                <IconButton
                  size="small"
                  sx={{ color: "black" }}
                  disabled={disableAction}
                  onClick={(e) => handleMoreClick(e, params?.row)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                slotProps={{
                  paper: {
                    sx: {
                      borderRadius: 0, // ✅ remove rounding completely
                      boxShadow: "none", // optional: remove shadow if you want a flat look
                    },
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    void handelOpenAttachment(params);
                  }}
                  sx={{ ...componentStyle.font.text }}
                >
                  View Attachment
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    navigate(
                      `/dashboard/v2/type-approval/traceTrack?project_id=${selectedProject.project_id
                      }&variant_id=${selectedVariant.variant_id
                      }&parent_page=${parentPage}&intr_id=${selectedTableRowData?.intr_id
                      }&tab=${getTabBasedOnTicketType()}`,
                    );
                  }}
                  sx={{ ...componentStyle.font.text }}
                >
                  View Track
                </MenuItem>

                {selectedProject?.status === PROJECT_STATUS.DRAFT && (
                  <>
                    {selectedTableRowData?.archive_status === "ARCHIVED" && (() => {
                      const canRetrieve = areAllTicketComponentsActive(selectedTableRowData);
                      return (
                        <Tooltip
                          title={
                            canRetrieve
                              ? ""
                              : "Cannot retrieve: one or more associated components are inactive or archived"
                          }
                          placement="left"
                        >
                          <span>
                            <MenuItem
                              disabled={!canRetrieve}
                              onClick={() => {
                                if (!canRetrieve) return;
                                setAnchorEl(null);
                                setConfirmActionType("retrieve");
                                setConfirmArchiveRetrieveDialogOpen(true);
                              }}
                              sx={{ ...componentStyle.font.text }}
                            >
                              Retrieve
                            </MenuItem>
                          </span>
                        </Tooltip>
                      );
                    })()}
                    {selectedTableRowData?.archive_status === "ACTIVE" &&
                      selectedProject?.status === PROJECT_STATUS.DRAFT &&
                      Boolean(selectedProject?.archive_vlc) && (
                      <MenuItem
                        onClick={() => {
                          setAnchorEl(null);
                          setConfirmActionType("archive");
                          setConfirmArchiveRetrieveDialogOpen(true);
                        }}
                        sx={{ ...componentStyle.font.text }}
                      >
                        Archive
                      </MenuItem>
                    )}
                  </>
                )}

                {enableSimulation &&
                  (selectedTableRowData?.simulation_file &&
                  selectedTableRowData.simulation_file.trim() !== "" ? (
                    <MenuItem
                      onClick={() => {
                        handleImageRowClick({ row: selectedTableRowData });
                        setAnchorEl(null);
                      }}
                      sx={{ ...componentStyle.font.text }}
                    >
                      View Simulation Files
                    </MenuItem>
                  ) : (
                    <MenuItem
                      onClick={(event) => {
                        handleSimulationClick(event, {
                          row: selectedTableRowData,
                        });
                        setAnchorEl(null);
                      }}
                      disabled={selectedTableRowData?.status !== 101}
                      sx={{ ...componentStyle.font.text }}
                    >
                      Upload Simulation File
                    </MenuItem>
                  ))}
              </Menu>
            </Box>
          ),
        },
      ]
      : []),
  ];

  useEffect(() => {
    if (ticketDetails) {
      const filteredTicketList = ticketDetails
        ?.map((item, index) => ({
          ...item,
          id: (page - 1) * rowsPerPage + index + 1,
          intr_id: item?.intr_id,
          external_url: item?.external_url,
          source_type: item?.source_type,
          tech_service_users: item?.tech_service_users,
          oem_users: item?.oem_users,
          users: item?.tech_service_users || item?.oem_users,
          ticket_id: item?.ticket_id,
          tickets_type: item?.tickets_type,
          status: item?.status,
          ticket_type: item?.ticket_type,
          ticket_status: item?.ticket_id || item?.status,
          ls_details: item?.ls_details,
          lr_details: item?.lr_details,
          swm_details: item?.swm_details,
          vehicle_component_details: item?.vehicle_component_details,
          ee_details: item?.ee_details,
          description: item?.description,
          reason: item?.reason,
          created_at: ConvertUTCToDate(
            item?.created_at,
            profileDetails?.profile?.profile?.region,
          ),
          created_by: item?.created_by,
          updated_at: ConvertUTCToDate(
            item?.updated_at,
            profileDetails?.profile?.profile?.region,
          ),
          updated_by: item?.updated_by,
          allData: item,
        }));

      setTableRowsData(filteredTicketList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketDetails]);

  useEffect(() => {
    void fetchUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handelOpenAttachment = async (data) => {
    console.log(data, "an attachment data");
    console.log(selectedTableRowData, "selectedTableRowData");
    const projectId = selectedTableRowData?.project_details?.project_id;
    const varientId = selectedTableRowData?.variant_details?.[0]?.variant_id;
    const intr_id = selectedTableRowData?.intr_id;
    const selectedComponentApi =
      getSelectedComponentApiByTicketType(selectedTableRowData);
    const apiDetails = typeApprovalAPI.get_user_dynamic_forms;
    setSelectedComponentFromGetApi(null);

    const [formResp, componentResp] = await Promise.all([
      Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            show_toast: false,
            params: {
              project_id: Number(projectId),
              variant_id: Number(varientId),
              intr_id: intr_id,
              page_number: 1,
              page_size: 100,
              is_ticket_required: true,
            },
          }),
        ),
      ),
      selectedComponentApi?.apiAction
        ? Promise.resolve(
          dispatch(
            HandleApiActions({
              ...selectedComponentApi.apiAction,
              show_toast: false,
              params: selectedComponentApi.params,
            }),
          ),
        )
        : Promise.resolve({ error: true }),
    ]);

    if (formResp.error) {
      setDynamicFormData(null);
    } else {
      setDynamicFormData(formResp?.data?.[0]);
    }

    if (componentResp?.error) {
      setSelectedComponentFromGetApi(null);
    } else {
      const componentList = Array.isArray(componentResp?.data)
        ? componentResp.data
        : [];
      const matchedComponent = selectedComponentApi?.matchFn
        ? componentList.find(selectedComponentApi.matchFn) || componentList?.[0]
        : componentList?.[0] || null;
      setSelectedComponentFromGetApi(matchedComponent);
    }

    setAttachmentDialogOpen(true);
    setAnchorEl(null);
  };

  console.log(dynamicFormData, "dynamicFormData,dynamicFormData");
  return (
    <Box sx={{ height: "450px", width: "100%" }}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ display: "none" }}
        multiple
      />
      <CustomDataGridV2
        rows={tableRowsData}
        columns={COLUMNS}
        rowCount={tableRowsData?.length}
        isMultiLine={true}
        showPagination={showPagination}
        enableMore={showPagination ? false : hasTicketNeedMore}
        page={page}
        rowsPerPage={rowsPerPage}
        handlePageChange={(page) => {
          setPage(page);
          fetchTickets({ page_number: page });
        }}
        totalItems={totalCount}
        handleMore={() => {
          let ticketType = tickets_type;
          navigate(
            `/dashboard/type-approval?project_id=${selectedProject.project_id}&linkModuleType=${ticketType}`,
          );
        }}
      />
      {console.log(
        "---selectedComponent ModuleViewMoreDialog---",
        selectedComponent,
      )}
      <ModuleViewMoreDialog
        isEEDialogOpen={isEEDialogOpen}
        toggleEEDialog={toggleEEDialog}
        selectedComponent={selectedComponent}
        linkApiProps={linkApiProps}
        apiAction={apiAction}
        apiDataKey={apiDataKey}
        type={moduleViewMoreType}
      />

      <CustomDialogBoxV2
        open={openModal}
        onClose={() => setOpenModal(false)}
        title={"View Image"}
      >
        <Box sx={{ width: "400px", height: "400px" }}>
          <img
            alt="Simulation file preview"
            src={imagePath}
            style={{
              height: "100%",
              width: "100%",
              objectFit: "contain", // Adjust object-fit as needed
            }}
          />
        </Box>
      </CustomDialogBoxV2>
      <CustomDialogBoxV2
        open={isLegalDialogOpen}
        onClose={toggleLegalDialog(false)}
        title={
          <Box
            sx={{
              fontWeight: "bold",
              color: "black",
              fontSize: "15px",
              display: "flex",
              width: "100%",
              justifyContent: "center",
              flex: 1,
            }}
          >
            Legal Details
          </Box>
        }
      >
        <Box sx={{ width: "650px" }}>
          <Box>
            {legalDialogData?.items3 && (
              <Box>
                {legalDialogData?.items3?.map((ls) => (
                  <Box
                    key={ls?.ls_id || ls?.standard_number || ls?.name}
                    sx={{
                      padding: "10px",
                      borderRadius: "10px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        color: "black",
                        fontSize: "15px",
                      }}
                    >
                      Legal Standard
                    </Typography>
                    <Box sx={{ gap: "10px" }}>
                      {[
                        { header: "ID", value: ls?.ls_id },
                        {
                          header: "Standard Number",
                          value: ls?.standard_number,
                        },
                        { header: "Name", value: ls?.name },
                        { header: "Revision", value: ls?.revision },
                        { header: "Description", value: ls?.description },
                        {
                          header: "Chapters",
                          value: ls?.chapters.join(", "),
                        }, // Display chapters
                      ]?.map((det) => (
                        <Typography key={det.header} sx={{ fontSize: "14px" }}>
                          {det?.header}: {det?.value}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <Box>
            {legalDialogData?.heading2 && (
              <Box>
                {legalDialogData?.items2?.map((lr) => (
                  <Box
                    key={lr?.lr_id || lr?.req_number || lr?.chapter}
                    sx={{
                      padding: "10px",
                      borderRadius: "10px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        color: "black",
                        fontSize: "15px",
                      }}
                    >
                      Legal Requirements
                    </Typography>
                    <Box sx={{ gap: "10px" }}>
                      {[
                        { header: "ID", value: lr?.lr_id },
                        { header: "Chapter", value: lr?.chapter },
                        {
                          header: "Requirement Number",
                          value: lr?.req_number,
                        },
                        {
                          header: "Description",
                          value: lr?.description,
                        },
                      ]?.map((det) => (
                        <Typography key={det.header} sx={{ fontSize: "14px" }}>
                          {det?.header}: {det?.value}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={attachmentDialogOpen}
        onClose={() => setAttachmentDialogOpen(false)}
        title="Attachment"
      >
        <Box sx={{ width: "800px", height: "500px", p: 0 }}>
          <TicketAttachment
            dynamicFormData={dynamicFormData}
            onClose={() => setAttachmentDialogOpen(false)}
            selectedTableRowData={selectedTableRowData}
            setSelectedTableRowData={setSelectedTableRowData}
            setDynamicFormData={setDynamicFormData}
            fetchTickets={fetchTickets}
            displayAddArtifact={parentPage !== "simulations"}
            selectedComponent={selectedComponentFromGetApi}
            componentType={getComponentTypeByTicketType()}
            tickets_type={tickets_type || selectedTableRowData?.tickets_type}
          />
        </Box>
      </CustomDialogBoxV2>

      {confirmArchiveRetrieveDialogOpen && (
        <CustomDialogBoxV2
          open={confirmArchiveRetrieveDialogOpen}
          onClose={() => setConfirmArchiveRetrieveDialogOpen(false)}
          title="Confirm Changes"
          // dialogRadius="0rem"
          successButtonName="Confirm"
          rejectButtonName="Cancel"
          actionButtonStyle="continuous"
          onClickSuccess={handleConfirmArchiveRetrieve}
          onClickReject={() => setConfirmArchiveRetrieveDialogOpen(false)}
        >
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "left", textAlign: "left", gap: 1 }}>
            <Typography>
              This change will affect the intersections.
            </Typography>
            <Typography>
              Are you sure you want to proceed?
            </Typography>
          </Box>
        </CustomDialogBoxV2>
      )}
    </Box>
  );
}

TicketsTable.propTypes = {
  tickets_type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ticketDetails: PropTypes.array,
  hasTicketNeedMore: PropTypes.bool,
  setSelectedTicketType: PropTypes.func,
  fetchTickets: PropTypes.func,
  selectedIntersectionIds: PropTypes.array,
  setSelectedIntersectionIds: PropTypes.func,
  enableSelect: PropTypes.bool,
  enableAction: PropTypes.bool,
  showPagination: PropTypes.bool,
  totalCount: PropTypes.number,
  enableSimulation: PropTypes.bool,
  getFilterDetails: PropTypes.func,
  parentPage: PropTypes.string,
};

VehicleSystemCell.propTypes = {
  row: PropTypes.object,
  supplier: PropTypes.bool,
  onViewMore: PropTypes.func,
  formatSupplierName: PropTypes.func,
};

VehicleComponentsCell.propTypes = {
  row: PropTypes.object,
  supplier: PropTypes.bool,
  onViewMore: PropTypes.func,
  formatSupplierName: PropTypes.func,
};

export default TicketsTable;
